import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { fullName } from '../lib';

interface StaffOption {
  id: string;
  firstName: string;
  lastName: string | null;
  email: string | null;
}

/**
 * Picks staff by user id — the only shape `ReportRecipient.userId` accepts.
 * There is no free-text address field anywhere in this form because there is
 * no column behind one (ADR-0013 §2.7): a report cannot be scheduled to
 * somebody who was never entitled to read it in the first place.
 */
export function StaffMultiSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data } = useApi<unknown>('/users', { limit: 50, search: search || undefined, isActive: true });
  const options = useMemo(() => rowsOf<StaffOption>(data).rows, [data]);
  const chosen = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]);

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <li
              key={id}
              className="flex items-center gap-1 rounded-full bg-accent-soft px-2 py-1 text-xs text-accent-soft-fg"
            >
              {chosen.get(id) ? fullName(chosen.get(id)!) : id}
              <button type="button" onClick={() => toggle(id)} aria-label={t('common.remove')}>
                <Icon as={X} size="xs" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('reporting.schedules.searchStaff')}
      />
      <ul className="max-h-40 space-y-0.5 overflow-y-auto rounded-lg border p-1">
        {options.map((option) => (
          <li key={option.id}>
            <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-hover">
              <input
                type="checkbox"
                checked={selected.includes(option.id)}
                onChange={() => toggle(option.id)}
              />
              <span className="truncate">{fullName(option)}</span>
              {option.email && <span className="truncate text-xs text-ink-muted">{option.email}</span>}
            </label>
          </li>
        ))}
        {!options.length && <li className="px-2 py-1.5 text-xs text-ink-muted">{t('common.noResults')}</li>}
      </ul>
    </div>
  );
}
